import React, { useState } from "react";
import {
    Box, Button, Typography, Grid, Tab, Tabs, Avatar, IconButton,
    Tooltip
} from "@mui/material";
import CustomButton from "components/Button";
import { FaPlus } from "react-icons/fa";
import AddNewSchedule from "./AddNewSchedule";
import TableComponent from "components/grid";
import CustomPagination from "components/CustomPagination";
import { useGetAllImplementationScheduleQuery, useGetWorkflowHistoryImplementationScheduleQuery } from "store/slices/apis/scheduleSlice";
import ViewSchedule from "./scheduleViewPopup";
import StatusBadge from "components/badge/StatusBadge";
import moment from "moment";
import { LuEye, LuRefreshCw } from "react-icons/lu";
import ScheduleActivitiesSection from "view/baselineSchedule/ScheduleActivitiesSection";
import WeeklyProgressUpdate from "./WeeklyProgressUpdate";
import { usePermission } from "hooks/usePermission";
import { UPDATE_SCHEDULE_ACTUALUPDATE, UPDATE_SCHEDULE_APPROVED, UPDATE_SCHEDULE_CREATE, UPDATE_SCHEDULE_INPROGRESS } from "helpers/permissionConfig";
import { useCheckMenuPermission } from "hooks/useCheckMenuPermission";

const EditIcon = ({ style }) => (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.5 2.5L8.5 0.5L0.5 8.5V10.5H2.5L10.5 2.5Z" fill={style?.fill || "#433F5E"} />
    </svg>
);

const ImplementationSchedule = () => {
    const [open, setOpen] = useState(false);
    const [isView, setIsView] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [value, setValue] = useState(0); // 0 = Approved, 1 = In progress
    const [selectedProjectId, setSelectedProjectId] = useState(null);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState("desc");
    const [sortBy, setSortBy] = useState("createdDate");
    const [showMspSchedule, setShowMspSchedule] = useState(false);
    //const showMspSchedule = true;

    const { hasPermission } = useCheckMenuPermission();
    const createAccess = usePermission(UPDATE_SCHEDULE_CREATE);

    const { data: scheduleList, isFetching, refetch } = useGetAllImplementationScheduleQuery({ type: "Schedule Update" });

    const {
        data: workflowHistoryData,
        isLoading: isHistoryLoading,
        isError: isHistoryError
    } = useGetWorkflowHistoryImplementationScheduleQuery(
        {
            recordId: selectedProjectId,
            moduleName: "Schedule Update"
        },
        {
            skip: !selectedProjectId
        }
    );
    React.useEffect(() => {
        // still loading / no data yet
        if (!scheduleList) {
            setShowMspSchedule(false);
            return;
        }

        let items = [];

        // case 1: API returns array directly
        if (Array.isArray(scheduleList)) {
            items = scheduleList;
        }
        // case 2: API returns { data: [...] }
        else if (Array.isArray(scheduleList.data)) {
            items = scheduleList.data;
        }
        // case 3: API returns { data: { items: [...] } }
        else if (Array.isArray(scheduleList.data?.items)) {
            items = scheduleList.data.items;
        }

        if (!items.length) {
            setShowMspSchedule(false);
            return;
        }

        // check if ANY item is approved (case-insensitive)
        const hasApproved = items.some((item) => {
            const status = item.status ?? item.Status ?? "";
            return typeof status === "string" && status.toLowerCase() === "approved";
        });

        setShowMspSchedule(hasApproved);
    }, [scheduleList]);

    const getAvatarColor = (title) => {
        const colors = ["#1976D2", "#388E3C", "#F57C00", "#7B1FA2", "#0288D1", "#C2185B"];
        const index = title ? title.charCodeAt(0) % colors.length : 0;
        return colors[index];
    };

    const handleAdd = () => {
        setOpen(!open);
    };

    const handleViewClick = (row) => {
        setIsView((prev) => !prev);
        setViewData(row);
        if (row.id || row.projectId) {
            setSelectedProjectId(row.id || row.masterScheduleId);
        }
    };
    const getSortedData = () => {
        if (!scheduleList?.data) return [];

        return [...scheduleList.data].sort((a, b) => {
            const aValue = a[sortBy];
            const bValue = b[sortBy];


            if (sortBy === 'createdDate') {
                const aTime = new Date(aValue).getTime();
                const bTime = new Date(bValue).getTime();
                return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
            }


            else if (!isNaN(aValue) && !isNaN(bValue)) {
                const aNum = Number(aValue);
                const bNum = Number(bValue);
                return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
            }


            else {
                const aStr = String(aValue || '');
                const bStr = String(bValue || '');
                return sortOrder === 'asc'
                    ? aStr.localeCompare(bStr)
                    : bStr.localeCompare(aStr);
            }
        });
    };

    const handleViewClose = () => {
        setIsView(false);
        setViewData(null);
        setSelectedProjectId(null);
    };

    const handleEditSchedule = (row) => {
        setIsEdit((prev) => !prev);
        setSelectedData(row);
    };

    const handleEditClose = () => {
        setIsEdit(false);
        setSelectedData(null);
    };

    const handleTabChange = (event, newValue) => {
        setValue(newValue);
        setPageNumber(1);
    };

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
    };

    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === "asc";
        setSortOrder(isAsc ? "desc" : "asc");
        setSortBy(property);
    };

    const handleRefresh = () => {
        refetch();
    };

    const columns = [
        {
            name: "Description",
            field: "description",
            width: "360px",
            renderer: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Avatar
                        sx={{
                            width: 30,
                            height: 30,
                            bgcolor: getAvatarColor(row.description),
                            color: "#fff",
                            fontSize: "14px",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                        }}
                    >
                        {row?.description?.[0] || "?"}
                    </Avatar>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: "16px",
                                maxWidth: "250px",
                                fontWeight: 500,
                                fontFamily: "Outfit",
                                color: "#433F5E",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {row?.description}
                        </Typography>
                    </Box>

                    <Box className="hoverButtons" sx={{ display: "none", gap: "10px" }}>
                        {/* <Button
                            onClick={() => handleEditSchedule(row)}
                            startIcon={<EditIcon style={{ width: 11, height: 11, fill: "#433F5E" }} />}
                            sx={{
                                backgroundColor: "#E7E7EB",
                                p: "2px 6px",
                                borderRadius: "5px",
                                fontSize: "11px",
                                fontWeight: 600,
                                textTransform: 'none'
                            }}
                        >
                            Edit
                        </Button> */}
                        <Button
                            onClick={() => handleViewClick(row)}
                            startIcon={<LuEye style={{ width: 12, height: 12 }} />}
                            sx={{
                                backgroundColor: "#E7E7EB",
                                p: "2px 6px",
                                borderRadius: "5px",
                                fontSize: "11px",
                                fontWeight: 600,
                                textTransform: "none",
                            }}
                        >
                            View
                        </Button>
                    </Box>
                </Box>
            ),
        },
        {
            name: "Status",
            field: "status",
            renderer: (row) => <StatusBadge status={row.status} />,
        },
        {
            name: "Created Date",
            field: "createdDate",
            renderer: (row) =>
                row?.createdDate ? moment(row.createdDate).format("DD MMM YYYY") : "N/A",
        },
        {
            name: "Created By",
            field: "createdBy",
        },
    ];

    // const tabs = ["Approved", "In progress", "Actual Update"]; // 🔁 swapped order
    const tabs = [{ label: "Approved", index: 0, permissionKey: [UPDATE_SCHEDULE_APPROVED] }, { label: "In progress", index: 1, permissionKey: [UPDATE_SCHEDULE_INPROGRESS] },{ label: "Actual Update", index: 2, permissionKey: [UPDATE_SCHEDULE_ACTUALUPDATE] }]

    return (
        <>
            <Grid container spacing={0}>
                <Grid size={12} sx={{ bgcolor: "#F6F8F9", p: 2, pb: 0 }}>
                    <Grid size={12}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: "6px",
                                width: "100%",
                            }}
                        >
                            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: "16px", sm: "18px", md: "18px" },
                                        lineHeight: "24px",
                                        color: "#252C32",
                                        fontFamily: "Outfit, sans-serif",
                                    }}
                                >
                                    Schedule
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: "18px", sm: "32px", md: "36px" },
                                        lineHeight: { xs: "16px", md: "48px" },
                                        color: "#252C32",
                                        fontFamily: "Outfit, sans-serif",
                                    }}
                                >
                                    Schedule
                                </Typography>
                            </Box>
                            {createAccess && <Tooltip title="Schedule Update" placement="top">
                                <CustomButton
                                    label="Add New"
                                    startIcon={<FaPlus />}
                                    onClick={handleAdd}
                                />
                            </Tooltip>}

                        </Box>
                    </Grid>
                </Grid>

                <Grid
                    size={12}
                    sx={{ pt: "30px", pl: "24px", pb: 3, backgroundColor: "#FFFFFF" }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            overflowX: "hidden",
                            whiteSpace: "nowrap",
                            p: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            "-webkit-overflow-scrolling": "touch",
                        }}
                    >
                        <Tabs
                            value={value}
                            onChange={handleTabChange}
                            TabIndicatorProps={{ style: { display: "none" } }}
                            variant="scrollable"
                            scrollButtons={false}
                            sx={{
                                justifyContent: "flex-start !important",
                                alignItems: "flex-start",
                                "& .MuiTabs-flexContainer": {
                                    justifyContent: "flex-start !important",
                                    gap: "30px",
                                },
                            }}
                        >
                            {tabs?.filter(item => hasPermission(item?.permissionKey) > 0)?.map((label, index) => (
                                <Tab
                                    key={index}
                                    label={
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 500,
                                                    fontSize: "14px",
                                                    lineHeight: "100%",
                                                    color:
                                                        value === index ? "#0E73F6" : "#7E7E7E",
                                                    fontFamily: "Outfit, sans-serif",
                                                }}
                                            >
                                                {label?.label}
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{
                                        textTransform: "none",
                                        minWidth: "auto",
                                        paddingBottom: "14px",
                                        paddingLeft: 0,
                                        paddingRight: 0,
                                        borderBottom:
                                            value === index
                                                ? "2px solid #0E73F6"
                                                : "2px solid transparent",
                                    }}
                                />
                            ))}
                        </Tabs>

                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <IconButton onClick={handleRefresh}>
                                <LuRefreshCw size={20} color="#48535B" />
                            </IconButton>
                        </Box>
                    </Box>
                </Grid>

                {/* 🔹 Tab content containers */}

                {/* Tab 0: Approved */}
                {value === 0 && (
                    <Grid size={12} sx={{ p: 2 }}>
                        {/* Your Approved UI container goes here */}
                        {/* <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: "1px solid #E4E7EC",
                                bgcolor: "#F9FAFB",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontWeight: 500,
                                    fontSize: "16px",
                                    mb: 1,
                                }}
                            >
                                Approved Schedules
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontWeight: 400,
                                    fontSize: "14px",
                                    color: "#667085",
                                }}
                            >
                                Design your separate Approved tab UI here.
                            </Typography>
                        </Box> */}
                        <ScheduleActivitiesSection showMspSchedule={showMspSchedule} />
                    </Grid>
                )}

                {/* Tab 1: In progress (existing table moved here) */}
                {value === 1 && (
                    <Grid size={12} sx={{ p: 2 }}>
                        <TableComponent
                            data={getSortedData()}
                            isLoading={isFetching}
                            columns={columns}
                            order={sortOrder}
                            orderBy={sortBy}
                            createSortHandler={handleRequestSort}
                        />

                        <CustomPagination
                            totalCount={scheduleList?.totalRecords || 0}
                            pageSize={pageSize}
                            page={pageNumber}
                            onPageChange={handlePageChange}
                            setCurrentPage={setPageNumber}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </Grid>
                )}

                {value === 2 && <WeeklyProgressUpdate />}
            </Grid>

            {open && (
                <AddNewSchedule
                    open={open}
                    handleClose={handleAdd}
                    title={"Schedule Update"}
                />
            )}

            {isView && (
                <ViewSchedule
                    open={isView}
                    onClose={handleViewClose}
                    cardData={viewData}
                    isHistoryLoading={isHistoryLoading}
                    isFalse={false}
                />
            )}
        </>
    );
};

export default ImplementationSchedule;